/** The Shows page: the list, its filters, the today strip and Went? prompts. */
import { getJson, qs, send } from "./request";
import type { ShowRow, ShowsDto, YearCount } from "./types";

export interface ShowFilters {
  q: string;
  year: number | null;
}
export const NO_FILTERS: ShowFilters = { q: "", year: null };
export const PAGE = 40;
export const MORE = 60;

export const isFiltering = (f: ShowFilters) => Boolean(f.q.trim() || f.year);

export const fetchShows = (f: ShowFilters) =>
  getJson<ShowsDto>(`/api/shows${qs({ q: f.q.trim(), year: f.year })}`, "Could not load shows");

/** Didn't go: a Not going RSVP, which also takes the event off the calendar. */
export const dismissWent = (upcomingId: number) =>
  send(`/api/upcoming/${upcomingId}/rsvp`, "PUT", { rsvp: "no" }, "Could not clear it");

export interface YearGroup {
  year: number;
  /** Every show that year, not only the ones on screen. */
  total: number;
  shows: ShowRow[];
}

/** Consecutive runs by year, in the order sent (newest first). */
export function groupByYear(shows: ShowRow[], years: YearCount[]): YearGroup[] {
  const totals = new Map(years.map((y) => [y.year, y.count]));
  const out: YearGroup[] = [];
  for (const s of shows) {
    const y = Number(s.date.slice(0, 4));
    const last = out[out.length - 1];
    if (last && last.year === y) last.shows.push(s);
    else out.push({ year: y, total: totals.get(y) ?? 0, shows: [s] });
  }
  return out;
}


/** Every show on one show's flyer, newest first (GET /api/shows/{id}/flyer). */
export interface SameFlyer {
  image_url: string | null;
  shows: ShowRow[];
}
export const fetchSameFlyer = (id: number) => getJson<SameFlyer>(`/api/shows/${id}/flyer`, "Could not load that flyer");

/** One Wall tile: a flyer and every show on it, newest first. */
export interface WallTile {
  url: string;
  shows: ShowRow[];
}
/**
 * The Wall: one tile per flyer. Shows that share one (a festival's lineup
 * poster, a run of nights) are one tile, placed at the newest show.
 * Shows without a flyer are not on the Wall.
 */
export function wallTiles(shows: ShowRow[]): WallTile[] {
  const out: WallTile[] = [];
  const byUrl = new Map<string, WallTile>();
  for (const s of shows) {
    if (!s.image_url) continue;
    const same = byUrl.get(s.image_url);
    if (same) same.shows.push(s);
    else {
      const tile = { url: s.image_url, shows: [s] };
      byUrl.set(s.image_url, tile);
      out.push(tile);
    }
  }
  return out;
}

export const yearsAgo = (date: string, today: string) => Number(today.slice(0, 4)) - Number(date.slice(0, 4));

/** Filters to and from the URL, so a filtered list survives a reload and a share. */
export function filtersFromParams(sp: URLSearchParams): ShowFilters {
  const year = Number(sp.get("year"));
  return {
    q: sp.get("q") ?? "",
    year: Number.isInteger(year) && year > 1900 ? year : null,
  };
}

export function applyFilters(sp: URLSearchParams, f: ShowFilters): URLSearchParams {
  const next = new URLSearchParams(sp);
  const set = (k: string, v: string | null) => (v ? next.set(k, v) : next.delete(k));
  set("q", f.q || null);
  set("year", f.year ? String(f.year) : null);
  return next;
}
